import type { AlarmReportRequest } from './alarm.types';

export type ApiPageBase = 'unknown' | 'zero' | 'one';

export type RequestBuilderParams = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  messageFilter: string;
  monitoredOnly: boolean;
  groupAlarms: boolean;
  priority: number | null;
  minPriority: number;
  maxPriority: number;
  siteIds: number[];
  categoryIds?: number[];
  alarmPathIds: number[];
  alarmStateIds?: number[];
  acknowledgedBy: string;
  includeStatistics: boolean;
  pageSize: number;
  uiPage: number;
  apiPageBase: ApiPageBase;
  forcedApiPageBase?: 'zero' | 'one';
};

const toPositiveNumbers = (values: number[]): number[] => values.filter(value => Number(value) > 0);

const toAlarmStateIds = (values: number[]): number[] => {
  const validStateIds = new Set([0, 1, 2, 3]);

  return values.filter(value => validStateIds.has(Number(value)));
};

const toOptionalPositiveNumber = (value: number): number | undefined => {
  if (Number(value) >= 1) {
    return Number(value);
  }

  return undefined;
};

const resolvePageBase = (forcedApiPageBase: 'zero' | 'one' | undefined, apiPageBase: ApiPageBase): 'zero' | 'one' => {
  if (forcedApiPageBase) {
    return forcedApiPageBase;
  }

  if (apiPageBase === 'zero') {
    return 'zero';
  }

  return 'one';
};

const toApiPageNumber = (pageBase: 'zero' | 'one', uiPage: number): number => {
  if (pageBase === 'zero') {
    return Math.max(0, uiPage - 1);
  }

  return Math.max(1, uiPage);
};

const toOptionalMessageFilter = (messageFilter: string): string | undefined => {
  if (messageFilter && messageFilter !== 'string') {
    return messageFilter;
  }

  return undefined;
};

const getPriorityFilterPayload = (
  priorities: number[],
  minPriority: number | undefined,
  maxPriority: number | undefined
): Pick<AlarmReportRequest, 'priorities'> | Pick<AlarmReportRequest, 'minPriority' | 'maxPriority'> => {
  if (priorities.length > 0) {
    return { priorities };
  }

  return { minPriority, maxPriority };
};

const toOptionalArray = (values: number[]): number[] | undefined => {
  if (values.length > 0) {
    return values;
  }

  return undefined;
};

const toOptionalAcknowledgedBy = (value: string): string | undefined => {
  const normalized = value.trim();
  return normalized || undefined;
};

export const buildAlarmReportRequest = ({
  startDate,
  startTime,
  endDate,
  endTime,
  messageFilter,
  monitoredOnly,
  groupAlarms,
  priority,
  minPriority,
  maxPriority,
  siteIds,
  categoryIds,
  alarmPathIds,
  alarmStateIds,
  acknowledgedBy,
  includeStatistics,
  pageSize,
  uiPage,
  apiPageBase,
  forcedApiPageBase,
}: RequestBuilderParams): AlarmReportRequest => {
  const startDateTime = new Date(`${startDate}T${startTime}:00.000`).toISOString();
  const endDateTime = new Date(`${endDate}T${endTime}:59.999`).toISOString();
  const sanitizedSiteIds = toPositiveNumbers(siteIds ?? []);
  const sanitizedAlarmPathIds = toPositiveNumbers(alarmPathIds ?? []);
  const sanitizedAlarmStateIds = toAlarmStateIds(alarmStateIds ?? []);
  const priorityValues: number[] = [];
  if (priority != null) {
    priorityValues.push(priority);
  }
  const sanitizedPriorities = toPositiveNumbers(priorityValues);
  const sanitizedMinPriority = toOptionalPositiveNumber(minPriority);
  const sanitizedMaxPriority = toOptionalPositiveNumber(maxPriority);
  const pageBase = resolvePageBase(forcedApiPageBase, apiPageBase);
  const apiPage = toApiPageNumber(pageBase, uiPage);
  const messageFilterValue = toOptionalMessageFilter(messageFilter);
  const priorityFilters = getPriorityFilterPayload(sanitizedPriorities, sanitizedMinPriority, sanitizedMaxPriority);
  const siteRegistryIds = toOptionalArray(sanitizedSiteIds);
  const siteCategoryIds = toOptionalArray(toPositiveNumbers(categoryIds ?? []));
  const requestAlarmPathIds = toOptionalArray(sanitizedAlarmPathIds);
  const requestAlarmStateIds = toOptionalArray(sanitizedAlarmStateIds);
  const requestAcknowledgedBy = toOptionalAcknowledgedBy(acknowledgedBy);
  const sortPayload: Pick<AlarmReportRequest, 'sortBy' | 'sortDirection'> | {} = {};
  if (!groupAlarms) {
    Object.assign(sortPayload, {
      sortBy: 'alarmTimestampMs',
      sortDirection: 'desc' as const,
    });
  }

  return {
    startDate: startDateTime,
    endDate: endDateTime,
    messageFilter: messageFilterValue,
    monitoredOnly,
    groupAlarms,
    ...priorityFilters,
    siteRegistryIds,
    siteCategoryIds,
    alarmPathIds: requestAlarmPathIds,
    alarmStateIds: requestAlarmStateIds,
    acknowledgedBy: requestAcknowledgedBy,
    includeStatistics,
    page: apiPage,
    pageSize,
    ...sortPayload,
  };
};
